import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Clock, FileText, Filter, Loader2, Mail, MapPin, Package, Search, Truck, User } from 'lucide-react';
import { getAllDispatches, markDispatchReceived } from '../../services/applicationService';
import { useAuth } from '../../context/AuthContext';

const modeIcons = {
  'Courier': Truck,
  'Speed Post': Mail,
  'Hand Delivery': MapPin,
  'Email': Mail,
};

const pill = (status) => status === 'received' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700';

const formatTS = (ts) => {
  if (!ts) return '-';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-IN');
};

const DispatchTracking = () => {
  const { userData: user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dispatches, setDispatches] = useState([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [submitting, setSubmitting] = useState(null); // dispatchId while marking received
  const [receiveForm, setReceiveForm] = useState({}); // keyed by dispatchId

  const load = async () => {
    setLoading(true);
    const res = await getAllDispatches(statusFilter === 'all' ? {} : { status: statusFilter });
    if (res.success) setDispatches(res.dispatches);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return dispatches;
    return dispatches.filter((d) => {
      const hay = [
        d.applicationId,
        d.hospitalId,
        (d.trackingId || ''),
        (d.mode || ''),
        (d.documents || []).map(x => x.title).join(' '),
        (d.notes || ''),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [dispatches, query]);

  const onMarkReceived = async (applicationId, dispatchId) => {
    const form = receiveForm[dispatchId] || {};
    const payload = {
      receivedBy: form.receivedBy?.trim() || (user?.profile?.name || 'finance'),
      receivedAt: form.receivedAt || new Date(),
      receiverNotes: form.receiverNotes || '',
    };
    setSubmitting(dispatchId);
    const res = await markDispatchReceived(applicationId, dispatchId, payload);
    setSubmitting(null);
    if (res.success) {
      // Clear form and reload
      setReceiveForm((prev) => ({ ...prev, [dispatchId]: {} }));
      load();
    } else {
      alert(res.error || 'Failed to update');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Dispatch Tracking</h2>
          <p className="text-sm text-gray-600">Track dispatched discharge documents and acknowledge receipt</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-3 space-y-3 md:space-y-0">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by application, mode, tracking, document, notes"
              className="w-full pl-9 pr-3 py-2 border rounded-md focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-md px-3 py-2"
            >
              <option value="all">All</option>
              <option value="dispatched">Dispatched</option>
              <option value="received">Received</option>
            </select>
          </div>
          <button onClick={load} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700">Refresh</button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6 flex items-center space-x-3">
          <Loader2 className="h-5 w-5 animate-spin text-teal-600" />
          <span className="text-gray-700">Loading dispatches…</span>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft border border-gray-200">
          <div className="divide-y">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No dispatches found</div>
            ) : (
              filtered.map((d) => {
                const Icon = modeIcons[d.mode] || Package;
                const docs = Array.isArray(d.documents) ? d.documents : [];
                const rf = receiveForm[d.id] || {};
                const isReceived = d.status === 'received';
                return (
                  <div key={d.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-100 rounded-md"><Icon className="h-5 w-5 text-gray-700" /></div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">{d.mode || '—'}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${pill(d.status)}`}>{d.status === 'received' ? 'Received' : 'Dispatched'}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="mr-3">App: {d.applicationId}</span>
                            {d.trackingId && <span className="mr-3">Tracking: {d.trackingId}</span>}
                            <span>Dispatched: {formatTS(d.dispatchedAt)}</span>
                          </div>
                          {docs.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {docs.map((x, idx) => (
                                <span key={idx} className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                  <FileText className="h-3 w-3 mr-1" />{x.title || x.type || 'Document'}
                                </span>
                              ))}
                            </div>
                          )}
                          {d.pod?.url && (
                            <div className="mt-2">
                              <a href={d.pod.url} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-blue-700 hover:underline">
                                <FileText className="h-3 w-3 mr-1" /> POD: {d.pod.name || 'View'}
                              </a>
                            </div>
                          )}
                          {d.notes && <div className="mt-2 text-xs text-gray-600">Notes: {d.notes}</div>}
                        </div>
                      </div>
                      {isReceived ? (
                        <div className="text-right text-xs text-gray-600">
                          <div className="flex items-center justify-end space-x-1 text-green-700">
                            <CheckCircle className="h-4 w-4" />
                            <span>Received</span>
                          </div>
                          <div>By: {d.receivedBy || '-'}</div>
                          <div>At: {formatTS(d.receivedAt)}</div>
                          {d.receiverNotes && <div className="mt-1">Note: {d.receiverNotes}</div>}
                        </div>
                      ) : (
                        <div className="w-full md:w-96 md:ml-4 mt-3 md:mt-0">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-600">Received By</label>
                              <div className="relative">
                                <User className="h-3 w-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                                <input
                                  className="w-full pl-7 pr-2 py-1.5 border rounded-md"
                                  value={rf.receivedBy || ''}
                                  onChange={(e) => setReceiveForm(prev => ({ ...prev, [d.id]: { ...prev[d.id], receivedBy: e.target.value } }))}
                                  placeholder="Finance team member"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">Received At</label>
                              <input
                                type="datetime-local"
                                className="w-full px-2 py-1.5 border rounded-md"
                                value={rf.receivedAt || ''}
                                onChange={(e) => setReceiveForm(prev => ({ ...prev, [d.id]: { ...prev[d.id], receivedAt: e.target.value } }))}
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-xs text-gray-600">Notes (optional)</label>
                              <input
                                className="w-full px-2 py-1.5 border rounded-md"
                                value={rf.receiverNotes || ''}
                                onChange={(e) => setReceiveForm(prev => ({ ...prev, [d.id]: { ...prev[d.id], receiverNotes: e.target.value } }))}
                                placeholder="Who picked, any remarks"
                              />
                            </div>
                          </div>
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => onMarkReceived(d.applicationId, d.id)}
                              disabled={!!submitting}
                              className="inline-flex items-center px-3 py-1.5 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
                            >
                              {submitting === d.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                              Mark Received
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchTracking;
